/* eslint-disable react/prop-types */
import moment from "moment";
import Badge from "components/atoms/Badge/Badge";
import { getFreightModeIcon } from "helpers/freightModeHelper";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { ReactComponent as ArrowDown } from "assets/images/icons/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "assets/images/icons/ArrowUp.svg";
import { ReactComponent as RateCardIcon } from "assets/images/icons/RateCard.svg";
import { ReactComponent as WarningIcon } from "assets/images/icons/Warning.svg";
import { ReactComponent as ApiIcon } from "assets/images/icons/Api.svg";
import { faQuestionCircle, faPaperclip } from "@fortawesome/free-solid-svg-icons";
import "../../../../common/QuotesDetail/quoteColumns.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormattedMessage } from "react-intl";
import { formatDatetime, formatPriceV2 } from "helpers/commonHelpers";
import QuoteActionButton from "./QuoteActionButton";

const renderColor = (value) => {
  if (!value) return "black";
  const onTime = parseFloat(value);
  if (onTime >= 0.9) return "#9ec728";
  if (onTime < 0.9 && onTime >= 0.8) return "#ff9000";
  if (onTime < 0.8) return "#EA432D";
};

function formatFlightTransits(array) {
  const combinedArray = [];
  let previousObject = null;

  array.forEach((object, index) => {
    if (index === 0 || object.label !== previousObject.label) {
      combinedArray.push(object);
    } else {
      const combinedObject = {
        label: object.label,
        departureTime: previousObject.value,
        arrivalTime: object.value,
        transit: previousObject.transit,
      };
      combinedArray.pop();
      combinedArray.push(combinedObject);
    }
    previousObject = object;
  });

  return combinedArray;
}

// Format data for the schedule column (data transit points)
export const formatScheduleArray = (schedule) => {
  let scheduleArray = [];
  scheduleArray.push({
    label: "Pickup date",
    value: schedule.quoted_pickup_date,
  });

  if (schedule.flights && schedule.flights.length > 0) {
    schedule.flights.forEach((flight) => {
      if (
        flight.airline ||
        flight.flight_number ||
        flight.departure_airport ||
        flight.arrival_airport ||
        flight.departure ||
        flight.arrival
      )
        scheduleArray.push({
          label: `${flight.departure_airport}`,
          labelV2: `${flight.departure_airport}`,
          value: flight.departure,
          transit: "Departure",
        });
      scheduleArray.push({
        label: `${flight.arrival_airport}`,
        labelV2: `${flight.arrival_airport}`,
        value: flight.arrival,
        transit: "Arrival",
      });
    });
    scheduleArray = formatFlightTransits(scheduleArray);
  }

  if (schedule.sailing) {
    if (
      schedule.sailing.departure_port_name ||
      schedule.sailing.arrival_port_name ||
      schedule.sailing.arrival ||
      schedule.sailing.departure
    ) {
      scheduleArray.push({
        label: `Departure port: ${schedule.sailing.departure_port_name || "No port available"}`,
        labelV2: schedule.sailing.departure_port,
        value: schedule.sailing.departure,
      });
      scheduleArray.push({
        label: `Arrival port: ${schedule.sailing.arrival_port_name || "No port available"}`,
        labelV2: schedule.sailing.arrival_port,
        value: schedule.sailing.arrival,
      });
    }
  }
  scheduleArray.push({
    label: "Delivery date",
    value: schedule.quoted_time_delivery_date,
  });
  return scheduleArray;
};

const getQuotesColumns = (
  intl,
  settings,
  shipmentData,
  requestStatus,
  id,
  approvalPending,
  fetchData,
  canViewQuotePricing,
  trafficLightEnabled,
  trafficLightBuckets,
) => {
  const columns = [
    {
      Header: intl.formatMessage({ id: "general.provider", defaultMessage: "Provider" }),
      accessor: (row) => [row.provider.name, row.provider.team],
      Cell: ({ value, row }) => (
        <div>
          <div className="font-b9 mb-2">{value[0]}</div>
          <div className="font-b2">
            {value[1]}
            {row.original.created_by_user_type === "system" && !row.original.general.is_rate_card_quote && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="freight-mode">
                    <span className="font-weight-normal">API quote</span>
                  </Tooltip>
                }
              >
                <ApiIcon className="ml-2" />
              </OverlayTrigger>
            )}
          </div>
          <div>
            {row.original.general.is_rate_card_quote && (
              <div className="mt-2">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="freight-mode">
                      <span className="font-weight-normal">Rate card quote</span>
                    </Tooltip>
                  }
                >
                  <RateCardIcon className="mb-1 mr-1" />
                </OverlayTrigger>
                <span className="text-secondary">{row.original.general.quote_title}</span>
              </div>
            )}
          </div>
        </div>
      ),
      disableDrag: true,
    },
    {
      Header: intl.formatMessage({ id: "general.quote", defaultMessage: "Quote" }),
      accessor: (row) => ({
        total_quoted_amount: row.financial.total_quoted_amount,
        total_quoted_amount_currency: row.financial.total_quoted_amount_currency,
        is_highest_price: row.financial.is_highest_price,
        is_lowest_price: row.financial.is_lowest_price,
        not_included_in_price: row.financial.not_included_in_price,
        client_price_per_mile: row.financial.client_price_per_mile,
        client_price_per_km: row.financial.client_price_per_km,
        total_quoted_amount_usd: row.financial.total_quoted_amount_usd,
        freight_mode_label: row.service.freight_mode_label,
        status: row.status,
      }),
      Cell: ({ value }) => {
        const { total_quoted_amount_usd, freight_mode_label, status } = value;
        const {
          totalQuotes: totalQuotesInTrafficLightBucket,
          lowerMarginUSD,
          upperMarginUSD,
        } = trafficLightBuckets?.[freight_mode_label] ?? {};
        const renderTrafficLight =
          trafficLightEnabled &&
          totalQuotesInTrafficLightBucket > 1 &&
          !["canceled", "canceled_by_provider"].includes(status);

        const trafficLightColor = (() => {
          if (!renderTrafficLight) return null;
          if (total_quoted_amount_usd < lowerMarginUSD) return "bg-mustard-yellow";
          if (total_quoted_amount_usd >= lowerMarginUSD && total_quoted_amount_usd <= upperMarginUSD)
            return "bg-mustard-yellow";
          if (total_quoted_amount_usd > upperMarginUSD) return "bg-peach-red";
          return null;
        })();

        return (
          <>
            {!canViewQuotePricing && <p className="font-weight-bold">HIDDEN</p>}
            {canViewQuotePricing && (
              <>
                <div className="d-flex align-items-center">
                  {renderTrafficLight && (
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Popover id="traffic-light-tooltip">
                          <Popover.Content>
                            <div className="font-b3">
                              <FormattedMessage
                                id="trafficLight.greenInfo"
                                defaultMessage="Green = Below quoted average lower margin"
                              />
                              <br />
                              <FormattedMessage
                                id="trafficLight.amberInfo"
                                defaultMessage="Amber = Within quoted average margin"
                              />
                              <br />
                              <FormattedMessage
                                id="trafficLight.redInfo"
                                defaultMessage="Red = Above quoted average upper margin"
                              />
                            </div>
                          </Popover.Content>
                        </Popover>
                      }
                    >
                      <div className={`traffic-light-info-container font-b4 ${trafficLightColor}`}>i</div>
                    </OverlayTrigger>
                  )}
                  <div style={{ marginRight: "4px", fontSize: "11px" }}>
                    {value.total_quoted_amount
                      ? `${formatPriceV2(value.total_quoted_amount)} ${value.total_quoted_amount_currency} `
                      : "-"}
                    {value.not_included_in_price && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="freight-mode">
                            <span className="font-weight-bold">
                              <FormattedMessage id="quote-request.costDetailNotInclude" defaultMessage="Not included" />
                            </span>
                            <br /> {value.not_included_in_price}
                          </Tooltip>
                        }
                      >
                        <WarningIcon className="mb-1 not-included-icon" />
                      </OverlayTrigger>
                    )}
                  </div>
                  <div>
                    {value.is_highest_price && (
                      <Badge
                        type="warning"
                        label={intl.formatMessage({
                          id: "general.highest",
                          defaultMessage: "Highest",
                        })}
                      />
                    )}
                    {value.is_lowest_price && (
                      <Badge
                        type="primary"
                        label={intl.formatMessage({
                          id: "general.lowest",
                          defaultMessage: "Lowest",
                        })}
                      />
                    )}
                  </div>
                </div>
                {value.client_price_per_mile && value.client_price_per_km && (
                  <div className="font-b3">
                    <span className="text-secondary">{`${formatPriceV2(value.client_price_per_mile)} ${
                      value.total_quoted_amount_currency
                    } /mi | ${formatPriceV2(value.client_price_per_km)} ${value.total_quoted_amount_currency} /km`}</span>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="freight-mode">
                          <span className="font-weight-bold">
                            <FormattedMessage
                              id="quote-request.roadFreightCosts"
                              defaultMessage="Total quoted amount, divided by the estimated distance in miles and km."
                            />
                            <br />
                          </span>
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="ml-2" />
                    </OverlayTrigger>
                  </div>
                )}
              </>
            )}
          </>
        );
      },
      disableDrag: true,
    },
    {
      Header: intl.formatMessage({ id: "general.schedule", defaultMessage: "Schedule" }),
      accessor: "test",
      Cell: ({ row }) => {
        const deliveryDatetime = row.original.schedule.quoted_time_delivery_date;
        const pickupDatetime = row.original.schedule.quoted_pickup_date;
        const earliestDelivery = row.original.schedule.earliest_delivery;
        // const earliestPickup = row.original.schedule.earliest_pickup;
        const shortestTransit = row.original.schedule.shortest_transit_time;
        const transitTime = moment(deliveryDatetime).diff(moment(pickupDatetime), "days");

        const dataPoints = formatScheduleArray(row.original.schedule);

        let lowestOnTime = null;
        if (row.original.schedule.flights) {
          row.original.schedule.flights.forEach((flight) => {
            const onTime = parseFloat(flight.performance.on_time);
            if (onTime && (lowestOnTime === null || onTime < lowestOnTime)) {
              lowestOnTime = onTime;
            }
          });
        }

        const Icon = getFreightModeIcon(
          row.original.service.freight_mode_label,
          row.original.service.freight_mode_type,
          true,
        );
        return (
          <div>
            <div>
              <div className="d-flex">
                <div style={{ fontSize: "11px" }}>
                  <div className="text-secondary">
                    <FormattedMessage id="general.pickup" defaultMessage="Pickup" />
                  </div>
                  {pickupDatetime === null ? "-" : <div>{formatDatetime(pickupDatetime)}</div>}
                </div>
                <div
                  className="d-flex flex-column justify-content-center align-items-center"
                  style={{
                    margin: "0 18px 0 18px",
                  }}
                >
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="freight-mode">
                        <span className="font-weight-normal">
                          {row.original.service.freight_mode_label}
                          {row.original.service.road_freight_type &&
                            `, ${row.original.service.road_freight_type.toUpperCase()}`}
                        </span>
                      </Tooltip>
                    }
                  >
                    <Icon
                      style={{
                        height: "18px",
                      }}
                    />
                  </OverlayTrigger>
                  <div className="font-weight-bold" style={{ fontSize: "9px" }}>
                    {deliveryDatetime === null || pickupDatetime === null ? "" : <span>{transitTime}d</span>}
                  </div>
                </div>
                <div style={{ fontSize: "11px" }}>
                  <div className="text-secondary">
                    <FormattedMessage id="general.delivery" defaultMessage="Delivery" />
                  </div>
                  {deliveryDatetime === null ? "Not specified" : <div>{formatDatetime(deliveryDatetime)}</div>}
                </div>
              </div>
            </div>
            <div>
              <div className="d-flex justify-content-between position-relative" style={{ width: "190px" }}>
                <div
                  style={{
                    width: `190px`,
                    height: "2px",
                    borderBottom: `2px dotted ${renderColor(lowestOnTime)}`,
                    position: "absolute",
                    top: 3,
                  }}
                />

                {dataPoints.map((point) => (
                  <div className="d-flex align-items-center" key={point.value}>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip id="freight-mode">
                          <span className="font-weight-normal">
                            {point.label !== "" && point.label !== "null" ? point.label : "Not available"}
                          </span>
                          <br />
                          {point.value && (
                            <span className="font-weight-normal">
                              {point.transit && `${point.transit}: `} {formatDatetime(point.value)}
                            </span>
                          )}
                          {point.departureTime && (
                            <div className="font-weight-normal">{`Arrival: ${formatDatetime(point.arrivalTime)}`}</div>
                          )}
                          {point.arrivalTime && (
                            <div className="font-weight-normal">{`Departure: ${formatDatetime(point.departureTime)}`}</div>
                          )}
                        </Tooltip>
                      }
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "white",
                          border: "2px solid black",
                          zIndex: "10",
                        }}
                      />
                    </OverlayTrigger>
                  </div>
                ))}
              </div>
              <div
                style={{ width: "190px" }}
                className="font-b9 d-flex flex-column align-items-center justify-content-center mt-1"
              >
                {/* {earliestPickup && <div>Earliest Pickup</div>} */}
                {earliestDelivery && (
                  <div>
                    <FormattedMessage
                      id="shipment-quotes.components.quotes.earliestDelivery"
                      defaultMessage="Earliest delivery"
                    />
                  </div>
                )}
                {shortestTransit && (
                  <div>
                    <FormattedMessage
                      id="shipment-quotes.components.quotes.shortestTransit"
                      defaultMessage="Shortest transit"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      },
      disableDrag: true,
    },
    {
      Header: intl.formatMessage({ id: "general.action", defaultMessage: "Action" }),
      accessor: (row) => [row.schedule.quoted_pickup_date, row.schedule.quoted_time_delivery_date],
      Cell: ({ row }) => {
        const quotesData = row.original;
        const offPlatformShipment = shipmentData?.shipment?.is_off_platform_shipment || false;
        return (
          <div>
            <QuoteActionButton
              approvalPending={approvalPending}
              fetchData={fetchData}
              quotesData={quotesData}
              requestStatus={requestStatus}
              offPlatformShipment={offPlatformShipment}
              quotationSettings={settings}
              id={id}
            />
          </div>
        );
      },
      disableDrag: true,
    },
    {
      Header: () => null,
      id: "expander",
      Cell: ({ row }) => {
        const attachments = row.original.other.attachments.length;
        return (
          <div className="d-flex align-items-center position-relative">
            {attachments !== 0 && (
              <FontAwesomeIcon
                icon={faPaperclip}
                className="mr-2"
                style={{ color: "#6c757d", position: "absolute", top: "50%", transform: "translateY(-50%)", left: "-15px" }}
              />
            )}
            {row.isExpanded ? <ArrowUp /> : <ArrowDown />}
          </div>
        );
      },
      disableDrag: true,
    },
  ];
  return columns;
};

export default getQuotesColumns;
